import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, X, ArrowRight, CheckCircle2, Star, MessageCircle,
  CreditCard, Banknote, Building2, ArrowLeft, Upload, GripVertical,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { BANK_INFO } from "@/data/services";
import { useCatalog } from "@/hooks/useCatalog";
import { useDraggable } from "@/hooks/useDraggable";
import { useFloatsVisibility } from "@/hooks/useFloatsVisibility";

const DISMISS_KEY = "ca_me_interesa_dismissed_v1";

/**
 * Floating "Me Interesa Lectura o Trabajo" button — visible only for
 * logged-in regular users. Opens a modal with the full catalog of services
 * (readings + witchcraft + pacts + vidente virtual). Each service card has
 * a "Solicitar" button that shows the payment methods for that service and
 * finally redirects to /mi-cuenta?tab=pays&service=<id> where the client
 * uploads their proof.
 *
 * UX:
 *   - Position: top-28 right-6 by default (safely below the top nav bar
 *     AND clear of the bottom-right stack: WhatsApp, chat widget, etc).
 *   - Draggable: pointer-drag to move anywhere on the screen. 6px threshold
 *     keeps taps working as clicks.
 *   - Dismissible: tiny X button hides the float permanently (localStorage).
 */
export const MeInteresaButton = () => {
  const { user, isUser, isAdmin } = useAuth();
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null); // service object being requested
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("ca:open-me-interesa", h);
    return () => window.removeEventListener("ca:open-me-interesa", h);
  }, []);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  const { hidden: globallyHidden } = useFloatsVisibility();
  const drag = useDraggable();
  const navigate = useNavigate();

  // Admins don't need to "solicitar" — they see everyone else's payments in
  // the admin panel. Also hide during the global floats-off state and when
  // the user has dismissed the button once.
  if (!user || !isUser || isAdmin || hidden || globallyHidden) return null;

  const goToPay = (svc) => {
    setOpen(false);
    setSelected(null);
    navigate(`/mi-cuenta?tab=pays&service=${svc.id}`);
  };

  const wa = (svc) => {
    const svcName = svc.name?.[lang] || svc.name?.es || svc.id;
    const msg = encodeURIComponent(`Hola CrisAngel, quiero solicitar: ${svcName}. ¿Me confirmas los pasos?`);
    return `${BANK_INFO.whatsappLink}?text=${msg}`;
  };

  const dismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    setHidden(true);
  };

  return (
    <>
      {/* Floating trigger — draggable pill anchored top-right by default */}
      <div
        {...drag}
        data-testid="me-interesa-float-root"
        className="fixed top-28 right-6 z-[80] cursor-grab active:cursor-grabbing"
      >
        <motion.div
          initial={{ opacity: 0, x: 30, scale: 0.6 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ delay: 0.6, type: "spring", stiffness: 220, damping: 18 }}
          className="flex items-center gap-1 pl-1.5 pr-1 py-1 rounded-full bg-gradient-to-r from-[#d4af37] via-[#f3d878] to-[#d4af37] shadow-[0_0_30px_rgba(212,175,55,0.5)] hover:shadow-[0_0_50px_rgba(212,175,55,0.85)] border border-yellow-200/40 select-none"
        >
          {/* Drag handle */}
          <div
            className="flex items-center justify-center text-black/50 hover:text-black/80 px-0.5"
            title="Arrastra para mover"
            data-testid="me-interesa-drag"
          >
            <GripVertical className="w-3 h-3.5" />
          </div>

          {/* Main click surface — navigates directly to /servicios per user request. */}
          <button
            type="button"
            data-testid="me-interesa-btn"
            onClick={() => navigate("/servicios")}
            className="flex items-center gap-2 pl-1 pr-3 py-1.5 rounded-full text-black font-bold text-xs uppercase tracking-widest hover:brightness-110 transition"
            title="Ver todos los servicios"
          >
            <Sparkles className="w-4 h-4" />
            <span>Me Interesa Lectura o Trabajo</span>
          </button>

          {/* Dismiss */}
          <button
            type="button"
            data-testid="me-interesa-dismiss"
            onClick={dismiss}
            aria-label="Ocultar este botón"
            title="Ocultar este botón"
            className="w-5 h-5 rounded-full bg-black/25 hover:bg-black/60 text-black hover:text-white flex items-center justify-center transition"
          >
            <X className="w-3 h-3" />
          </button>
        </motion.div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="me-interesa-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[145] bg-black/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
            onClick={() => { setOpen(false); setSelected(null); }}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl max-h-[92vh] rounded-3xl overflow-hidden border border-[#d4af37]/40 shadow-[0_0_80px_rgba(212,175,55,0.28)] flex flex-col"
              style={{
                background:
                  "radial-gradient(circle at 0% 0%, rgba(120,40,160,0.32) 0%, transparent 55%), radial-gradient(circle at 100% 100%, rgba(212,175,55,0.15) 0%, transparent 55%), linear-gradient(180deg, #0b0612 0%, #050307 100%)",
              }}
            >
              <button
                type="button"
                data-testid="me-interesa-close"
                onClick={() => { setOpen(false); setSelected(null); }}
                aria-label="Cerrar"
                className="absolute top-3 right-3 z-30 w-9 h-9 rounded-full bg-black/60 hover:bg-black/90 border border-white/15 text-neutral-200 hover:text-white flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>

              {selected ? (
                <PaymentMethodsPanel
                  svc={selected}
                  lang={lang}
                  onBack={() => setSelected(null)}
                  onPay={() => goToPay(selected)}
                  waLink={wa(selected)}
                />
              ) : (
                <ServicesCatalog
                  lang={lang}
                  onSolicitar={(svc) => setSelected(svc)}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};


/* ----------------------------- Services catalog ---------------------------- */

const CATEGORY_LABELS = {
  lectura:    { es: "Lecturas",   en: "Readings",   color: "border-[#d4af37]/40 bg-[#d4af37]/10 text-[#d4af37]" },
  hechiceria: { es: "Hechicería", en: "Witchcraft", color: "border-purple-500/40 bg-purple-500/10 text-purple-300" },
  pactos:     { es: "Pactos",     en: "Pacts",      color: "border-rose-500/40 bg-rose-500/10 text-rose-300" },
};

const ServicesCatalog = ({ lang, onSolicitar }) => {
  const { visible } = useCatalog();
  const grouped = useMemo(() => {
    const g = {};
    visible.forEach((s) => {
      const c = s.category || "otro";
      (g[c] ||= []).push(s);
    });
    return g;
  }, [visible]);

  return (
    <div className="flex flex-col min-h-0">
      {/* Header */}
      <div className="px-6 pt-6 pb-3 border-b border-white/5">
        <div className="text-[10px] uppercase tracking-[0.35em] text-[#d4af37]/80 mb-1 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> Catálogo completo
        </div>
        <h2 className="font-serif text-2xl text-white">Elige tu Lectura o Trabajo</h2>
        <p className="text-xs text-neutral-400 mt-1">
          Toca &quot;Solicitar&quot; en el servicio que te interesa. Verás los métodos de pago y podrás subir tu comprobante en segundos.
        </p>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto ca-visible-scroll p-4 sm:p-6 space-y-6">
        {Object.entries(grouped).map(([cat, list]) => {
          const meta = CATEGORY_LABELS[cat] || { es: cat, en: cat, color: "border-white/20 bg-white/5 text-neutral-300" };
          return (
            <div key={cat} data-testid={`svc-cat-${cat}`}>
              <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest border rounded-full px-2.5 py-1 font-bold ${meta.color} mb-3`}>
                {meta[lang] || meta.es}
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((s) => (
                  <ServiceCard key={s.id} svc={s} lang={lang} onSolicitar={() => onSolicitar(s)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ServiceCard = ({ svc, lang, onSolicitar }) => {
  const name = svc.name?.[lang] || svc.name?.es || svc.id;
  const subtitle = svc.subtitle?.[lang] || svc.subtitle?.es || "";
  const description = svc.description?.[lang] || svc.description?.es || "";
  const features = svc.features?.[lang] || svc.features?.es || [];
  const badge = svc.badge?.[lang] || svc.badge?.es || null;
  const modalities = svc.modalities || [];
  const priceMXN = svc.priceMXN;
  const priceUSD = svc.priceUSD;

  return (
    <div
      data-testid={`svc-card-${svc.id}`}
      className="rounded-2xl border border-white/10 hover:border-[#d4af37]/50 bg-black/40 p-4 flex flex-col transition-colors"
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0 flex-1">
          <h3 className="font-serif text-base text-white leading-tight">{name}</h3>
          {subtitle && <div className="text-[10px] text-[#d4af37]/80 uppercase tracking-widest mt-0.5">{subtitle}</div>}
        </div>
        {badge && (
          <span className="text-[9px] uppercase tracking-widest bg-[#d4af37] text-black px-1.5 py-0.5 rounded-full font-bold flex-shrink-0">
            {badge}
          </span>
        )}
      </div>

      {description && (
        <p className="text-[11px] text-neutral-300 leading-snug mt-2">{description}</p>
      )}

      {/* Pricing */}
      <div className="mt-3 rounded-lg border border-[#d4af37]/25 bg-[#d4af37]/5 p-2.5">
        {modalities.length > 0 ? (
          <div className="space-y-1">
            {modalities.map((m) => (
              <div key={m.id} className="flex items-center justify-between text-[11px]">
                <span className="text-neutral-300">{m.label?.[lang] || m.label?.es}</span>
                <span className="font-mono text-[#f3d878] font-semibold">
                  ${Number(m.priceMXN).toLocaleString("es-MX")} MXN
                  <span className="text-neutral-500"> / ${Number(m.priceUSD).toLocaleString("en-US")} USD</span>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-baseline gap-1">
            <span className="font-serif text-lg text-white">${Number(priceMXN).toLocaleString("es-MX")}</span>
            <span className="text-[10px] text-neutral-400">MXN</span>
            <span className="text-[10px] text-neutral-500 ml-1">/ ${Number(priceUSD).toLocaleString("en-US")} USD</span>
          </div>
        )}
      </div>

      {/* Features */}
      {features.length > 0 && (
        <ul className="mt-2.5 space-y-0.5">
          {features.slice(0, 5).map((f, i) => (
            <li key={i} className="flex items-start gap-1.5 text-[10px] text-neutral-300">
              <Star className="w-2.5 h-2.5 text-[#d4af37] fill-[#d4af37] flex-shrink-0 mt-0.5" />
              <span className="leading-tight">{f}</span>
            </li>
          ))}
          {features.length > 5 && (
            <li className="text-[10px] text-neutral-500 italic pl-4">+{features.length - 5} más…</li>
          )}
        </ul>
      )}

      <button
        type="button"
        data-testid={`svc-solicitar-${svc.id}`}
        onClick={onSolicitar}
        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-gradient-to-r from-[#d4af37] via-[#f3d878] to-[#d4af37] text-black font-bold text-[10px] uppercase tracking-widest shadow-[0_0_20px_rgba(212,175,55,0.25)] hover:shadow-[0_0_35px_rgba(212,175,55,0.5)] transition-all"
      >
        Solicitar <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
};


/* --------------------------- Payment methods panel -------------------------- */

const PaymentMethodsPanel = ({ svc, lang, onBack, onPay, waLink }) => {
  const name = svc.name?.[lang] || svc.name?.es || svc.id;

  return (
    <div className="flex flex-col min-h-0">
      {/* Header */}
      <div className="px-6 pt-6 pb-3 border-b border-white/5 flex items-center gap-2">
        <button
          type="button"
          data-testid="pmpanel-back"
          onClick={onBack}
          className="w-8 h-8 rounded-full bg-black/50 border border-white/10 hover:border-[#d4af37]/60 text-neutral-200 hover:text-white flex items-center justify-center"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#d4af37]/80">Métodos de pago</div>
          <h2 className="font-serif text-xl text-white leading-tight">{name}</h2>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto ca-visible-scroll p-4 sm:p-6">
        <p className="text-xs text-neutral-300 leading-relaxed mb-4">
          Elige el método de pago que prefieras y realiza el pago. Después, presiona <b className="text-[#d4af37]">&quot;Ya pagué — subir comprobante&quot;</b> para registrarlo. Cristopher lo revisará y activará tu servicio en minutos.
        </p>

        <div className="grid md:grid-cols-2 gap-3">
          {/* Transferencia MX */}
          <div className="rounded-xl border border-[#d4af37]/30 bg-black/40 p-4" data-testid="pm-bank-mx">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-[#d4af37]" />
              <div className="text-[11px] uppercase tracking-widest text-[#d4af37] font-bold">Transferencia México</div>
            </div>
            <dl className="text-[11px] space-y-1">
              <div className="flex justify-between gap-2">
                <dt className="text-neutral-400">Banco</dt>
                <dd className="text-white font-mono">{BANK_INFO.bank}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-neutral-400">CLABE</dt>
                <dd className="text-white font-mono select-all">{BANK_INFO.clabe}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-neutral-400">Tarjeta</dt>
                <dd className="text-white font-mono select-all">{BANK_INFO.card}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-neutral-400">Beneficiario</dt>
                <dd className="text-white text-right">{BANK_INFO.beneficiary}</dd>
              </div>
            </dl>
          </div>

          {/* PayPal */}
          <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4" data-testid="pm-paypal">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-4 h-4 text-blue-300" />
              <div className="text-[11px] uppercase tracking-widest text-blue-300 font-bold">PayPal</div>
            </div>
            <div className="text-[11px] text-neutral-300 mb-1">Envía a:</div>
            <div className="text-sm text-white font-mono select-all break-all">{BANK_INFO.paypalEmail}</div>
          </div>

          {/* Remesas internacionales */}
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 md:col-span-2" data-testid="pm-remittance">
            <div className="flex items-center gap-2 mb-2">
              <Banknote className="w-4 h-4 text-emerald-300" />
              <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-bold">Envío internacional (Remesas)</div>
            </div>
            <div className="text-[11px] text-neutral-300 leading-relaxed">
              Envía por <b className="text-white">Remitly, Wise, Xoom, Ria, Western Union o MoneyGram</b>. Retiro en efectivo en:
              <div className="mt-1.5 space-y-0.5 font-mono text-white">
                <div>👤 {BANK_INFO.internationalRemittance.pickupName}</div>
                <div>🏪 Cadena: {BANK_INFO.internationalRemittance.pickupChain}</div>
                <div>📍 {BANK_INFO.internationalRemittance.pickupState}</div>
                <div>📱 {BANK_INFO.internationalRemittance.pickupPhone}</div>
              </div>
            </div>
          </div>
        </div>

        {/* WhatsApp helper */}
        <a
          href={waLink}
          target="_blank"
          rel="noreferrer"
          data-testid="pm-whatsapp"
          className="mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 rounded-full px-3 py-1.5 bg-emerald-500/10"
        >
          <MessageCircle className="w-3.5 h-3.5" /> Necesito ayuda por WhatsApp
        </a>
      </div>

      {/* Fixed CTA footer */}
      <div className="px-4 sm:px-6 py-3 border-t border-white/5 bg-black/50">
        <button
          type="button"
          data-testid="pm-goto-mispagos"
          onClick={onPay}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-500 text-black font-bold text-sm uppercase tracking-widest shadow-[0_0_30px_rgba(52,211,153,0.35)] hover:shadow-[0_0_45px_rgba(52,211,153,0.65)] transition-all"
        >
          <Upload className="w-4 h-4" /> Ya pagué — subir comprobante <ArrowRight className="w-4 h-4" />
        </button>
        <div className="text-[10px] text-neutral-500 text-center mt-2 flex items-center justify-center gap-1">
          <CheckCircle2 className="w-3 h-3 text-emerald-400" />
          Tu servicio se activa cuando Cristopher aprueba el comprobante.
        </div>
      </div>
    </div>
  );
};

export default MeInteresaButton;
